import { Building06, HomeLine, Package, PackagePlus, BookClosed, File05 } from '@untitled-ui/icons-react/build/cjs';
import React, { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import BadgeCmp from 'src/components/BadgeCmp';

import bookClosed from '../../assets/img/icons/filled-icons/book-closed.svg';
import building06 from '../../assets/img/icons/filled-icons/building-06.svg';
import file05 from '../../assets/img/icons/filled-icons/file-05.svg';
import globe01 from '../../assets/img/icons/filled-icons/globe-01-fill.svg';
import globeStroke from '../../assets/img/icons/filled-icons/globe-01.svg';
import homeLine from '../../assets/img/icons/filled-icons/home-line.svg';
import packagePlus from '../../assets/img/icons/filled-icons/package-plus.svg';
import packageIcon from '../../assets/img/icons/filled-icons/package.svg';
import { ROUTES } from '../../constants/routes';
import { type RootState } from '../../redux/store';
import { hasMenuPermission } from '../../utils/permissions';

interface IProps {
    isSidebarOpen: boolean;
    setIsSidebarOpen?: (isOpen: boolean) => void;
}

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }: IProps) => {
    const { t } = useTranslation();
    const useIdNum = useId();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const sidebarRef = useRef<HTMLElement>(null);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const [counts] = useState<any>({});

    const checkMode = window.MODE === 'production' || window.MODE === 'staging' ? false : true;

    // Utility function to check if screen is small (mobile/tablet)
    const isSmallScreen = () => window.innerWidth < 768; // md breakpoint in Tailwind is 768px

    // Function to handle navigation and close sidebar on small screens
    const handleNavigation = () => {
        if (isSmallScreen() && isSidebarOpen) {
            setIsSidebarOpen?.(false);
        }
    };

    const mainOptions = useMemo(
        () => [
            {
                path: ROUTES.HOME,
                displayName: t('Sidebar.Home'),
                Icon: <img src={homeLine} alt="home" className="link_icon" />,
                ActiveIcon: <HomeLine className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Home'),
            },
            {
                path: ROUTES.MY_QUOTE,
                displayName: t('Sidebar.Shop'),
                Icon: <img src={packagePlus} alt="shop" className="link_icon" />,
                ActiveIcon: <PackagePlus className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Shop'),
            },
            {
                path: ROUTES.ORDERS,
                displayName: t('Sidebar.My_Orders'),
                Icon: <img src={packageIcon} alt="orders" className="link_icon" />,
                ActiveIcon: <Package className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'My Orders'),
            },
            {
                path: ROUTES.TRACK,
                displayName: 'Track',
                Icon: <img src={globe01} alt="track" className="link_icon" />,
                ActiveIcon: <img src={globeStroke} alt="track" className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Track'),
            },
            {
                path: ROUTES.DOCUMENTATION_HUB,
                displayName: t('Sidebar.Document_Hub'),
                Icon: <img src={file05} alt="documents" className="link_icon" />,
                ActiveIcon: <File05 className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Document Hub'),
            },
            {
                path: ROUTES.ORGANIZATION,
                displayName: t('Sidebar.Organization'),
                Icon: <img src={building06} alt="organization" className="link_icon" />,
                ActiveIcon: <Building06 className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Organization'),
            },
            {
                path: ROUTES.ADDRESS_BOOK,
                displayName: t('Sidebar.Address_Book'),
                Icon: <img src={bookClosed} alt="address book" className="link_icon" />,
                ActiveIcon: <BookClosed className="link_icon" />,
                permission: hasMenuPermission(currentUser?.role, 'Address Book'),
            },
        ],
        [currentUser?.role]
    );

    const [containerHeights, setContainerHeights] = useState<any>({});

    const toggleHeight = (index: string, isKeepOpen: boolean = false, subMenu?: any[]) => {
        const updatedContainerHeights = {} as any;
        const isCurrentlyOpen = !!containerHeights[index];
        const shouldOpen = !isCurrentlyOpen || isKeepOpen;

        updatedContainerHeights[index] = shouldOpen ? document.getElementById(`container${index}`)?.scrollHeight : 0;

        setContainerHeights(updatedContainerHeights);

        // Navigate to first accessible sub-menu item when opening the menu
        // Only auto-navigate on large screens, let users choose on small screens
        if (shouldOpen && !isKeepOpen && subMenu && !isSmallScreen()) {
            const firstAccessibleSubMenu = subMenu.find((item: any) => item.subPermission);

            if (firstAccessibleSubMenu) {
                navigate(firstAccessibleSubMenu.subPath);
            }
        }
    };

    const closeAllSubMenus = () => {
        setContainerHeights({});
    };

    const matchPattern = (pattern: any, subPath: any) => {
        const patternSegments = pattern.split('/');
        const pathnameSegments = subPath.split('/');

        const patternLength = patternSegments.length;
        const pathnameLength = pathnameSegments.length;

        if (pathnameLength < patternLength || pathnameLength > patternLength) return false;

        for (let i = 0; i < patternLength; i++) {
            const patternSegment = patternSegments[i];
            const pathnameSegment = pathnameSegments[i];

            if (patternSegment.startsWith(':')) continue;

            if (patternSegment !== pathnameSegment) return false;
        }

        return true;
    };

    const handleMenuAndSubMenuActivation = () => {
        mainOptions.forEach((mainOption: any) => {
            const pathNameMain = pathname.split('/').filter((ptn) => ptn);
            const isSubActive = mainOption.subMenu
                ? mainOption.subMenu.find(
                      (item: any) =>
                          (pathname === '/' && item.subPath === '/quotes/dashboard' && !checkMode) ||
                          item.subPath === pathname ||
                          (item.subPath === '/' && item.subPathAlt === pathNameMain?.[0]) ||
                          (item.subPath === '/' && pathNameMain?.[0] === 'login') ||
                          item.childPathNames?.includes(pathname) ||
                          item.childPathNames?.some((child: any) => matchPattern(child, pathname))
                  )
                : undefined;

            if (isSubActive) {
                toggleHeight(mainOption.displayName, true);
            }
        });
    };

    useEffect(() => {
        handleMenuAndSubMenuActivation();
    }, [pathname]);

    // Handle clicks outside sidebar to close it on small screens
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Only proceed if sidebar is open and we're on small screen
            if (!isSidebarOpen || !isSmallScreen()) {
                return;
            }

            // Don't close if clicking inside the sidebar
            if (sidebarRef.current?.contains(target)) {
                return;
            }

            // Don't close if clicking on the toggle button
            if (target.closest('[data-sidebar-toggle="true"]')) {
                return;
            }

            // Close the sidebar
            setIsSidebarOpen?.(false);
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSidebarOpen, setIsSidebarOpen]);

    return (
        <aside
            ref={sidebarRef}
            className={`w-[var(--layout-sidebar-width)] py-3 [&>*]:px-3 border-r border-utilityGray200 bg-gray900 overflow-auto scrollbar-hide flex flex-col sidebar-wrapper ${
                isSidebarOpen ? 'close-sidebar' : 'open-sidebar'
            }`}
            aria-label="SideNav"
        >
            <ul key={`sidebar_${useIdNum}`} className="flex-1 overflow-auto scrollbar-hide">
                {mainOptions.map((option: any, index: number) => {
                    const { hasSubMenu, path, Icon, ActiveIcon, displayName, permission, subMenu, isShowCount, countKey } = option;
                    const pathNameMain = pathname.split('/').filter((ptn) => ptn);
                    const isSubActive = subMenu
                        ? subMenu.find(
                              (item: any) =>
                                  (pathname === '/' && item.subPath === '/quotes/dashboard' && !checkMode) ||
                                  item.subPath === pathname ||
                                  item.subPath === pathNameMain ||
                                  (item.subPath === '/' && item.subPathAlt === pathNameMain?.[0]) ||
                                  item.childPathNames?.includes(pathname) ||
                                  item.childPathNames?.some((child: any) => matchPattern(child, pathname))
                          )
                        : undefined;

                    if (!permission) {
                        return <Fragment key={`nav_${displayName}_${index}`}></Fragment>;
                    }
                    const activeSubIndex = subMenu
                        ? subMenu.findIndex((sub: any) => sub.subPath === pathname || sub.childPathNames?.includes(pathname) || sub.childPathNames?.some((child: any) => matchPattern(child, pathname)))
                        : -1;

                    return (
                        <li key={`nav_${displayName}_${index}`} className={`relative ${containerHeights[displayName] && isSubActive ? 'sidebar-sub-menu-open' : ''} `}>
                            {hasSubMenu ? (
                                <div onClick={() => toggleHeight(displayName, false, subMenu)} className={`wal__sidebar_link `}>
                                    <div className="module-icon-wrap">{pathname === path || isSubActive?.subPath === pathname ? ActiveIcon : Icon}</div>
                                    <div className="module-name">{displayName}</div>
                                </div>
                            ) : (
                                <NavLink
                                    to={path}
                                    onClick={() => {
                                        toggleHeight(displayName);
                                        closeAllSubMenus();
                                        handleNavigation();
                                    }}
                                    className={({ isActive }) => `wal__sidebar_link ${(isActive || isSubActive?.subPath === pathname) && '!bg-white shadow-xs'} `}
                                    // eslint-disable-next-line react/no-children-prop
                                    children={() => (
                                        <>
                                            <div className="module-icon-wrap">{pathname === path || isSubActive?.subPath === pathname ? ActiveIcon : Icon}</div>
                                            <div className="module-name">{displayName}</div>
                                            {isShowCount && counts[countKey] && counts[countKey] > 0 ? (
                                                <BadgeCmp
                                                    style="modern"
                                                    type="primary"
                                                    isHidePillDot
                                                    mainClassName="!rounded-md !px-1 min-w-5 justify-center !bg-white !shadow-xs !border-borderPrimary min-w-[23px]"
                                                    badgeTextColor="!text-primary !font-medium"
                                                >
                                                    {counts[countKey] > 99 ? '99' : counts[countKey]}
                                                </BadgeCmp>
                                            ) : (
                                                ''
                                            )}
                                        </>
                                    )}
                                />
                            )}

                            {subMenu && hasSubMenu && (
                                <div
                                    className={`overflow-hidden transition-all duration-100 ${containerHeights[displayName] && isSubActive ? 'mb-2' : ''} `}
                                    style={{
                                        maxHeight: containerHeights[displayName] || 0,
                                    }}
                                    id={`container${displayName}`}
                                >
                                    {subMenu
                                        .filter((item: any) => item.subPermission)
                                        .map((item: any, idx: number) => {
                                            const { subPath, subDisplayName, isShowCount: isShowCountSubMenu, countKey: countKeySubMenu } = item;
                                            const subMenuLength = subMenu.filter((itemVal: any) => itemVal.subPermission)?.length;

                                            return (
                                                <div key={`sub_nav_${subPath}`} className={``}>
                                                    <NavLink
                                                        to={subPath}
                                                        onClick={handleNavigation}
                                                        className={({ isActive }) =>
                                                            `sub-module-wrap ${isActive || isSubActive?.subPath === subPath ? 'active-sub-module' : ''} ${
                                                                !isActive && activeSubIndex > idx && 'between-active-sub-module'
                                                            } ${isSubActive?.subPath === subPath && 'text-grayLight900'}`
                                                        }
                                                    >
                                                        <div className={`sub-module-icon ${idx === 0 ? 'is_first_child' : ''} ${idx + 1 === subMenuLength ? 'is_last_child' : ''}`}></div>

                                                        <span className="sub-module-name">{subDisplayName}</span>
                                                        {isShowCountSubMenu && counts[countKeySubMenu] && counts[countKeySubMenu] > 0 ? (
                                                            <BadgeCmp
                                                                style="modern"
                                                                type="primary"
                                                                isHidePillDot
                                                                mainClassName="!rounded-md !px-1 min-w-5 justify-center !bg-white !shadow-xs !border-borderPrimary min-w-[23px]"
                                                                badgeTextColor="!text-primary !font-medium"
                                                            >
                                                                {counts[countKeySubMenu] > 99 ? '99' : counts[countKeySubMenu]}
                                                            </BadgeCmp>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </NavLink>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </aside>
    );
};

export default Sidebar;
